import { createStyles, type Theme } from '@material-ui/core/styles';

// A section that is an accordion closed by default (timeline, artifacts); the
// summary row keeps the same title rule as every other section so it still
// reads as a sibling.
const timelineDisclosureStyles = (theme: Theme) =>
  createStyles({
    collapsibleSummary: {
      padding: 0,
      minHeight: 0,
      '&$collapsibleExpanded': {
        minHeight: 0,
        paddingBottom: theme.spacing(1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '&:focus-visible': {
        outline: `3px solid ${theme.palette.secondary.main}`,
        outlineOffset: 2,
      },
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    },
    collapsibleSummaryContent: {
      margin: 0,
      minWidth: 0,
      overflowWrap: 'anywhere',
      '&$collapsibleExpanded': { margin: 0 },
    },
    collapsibleExpanded: {},
    collapsibleDetails: {
      display: 'block',
      padding: theme.spacing(1, 0, 0),
      '& > div': { width: '100%', minWidth: 0 },
    },
    timelineToolbarActions: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  });

const timelineLayoutStyles = (theme: Theme) =>
  createStyles({
    timelineToolbar: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      margin: theme.spacing(2, 0),
    },
    timelineFilters: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
    },
    timelineFilterActions: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(1),
      gridColumn: '1 / -1',
    },
    timelineList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'grid',
      gap: theme.spacing(1),
    },
  });

const timelineContentStyles = (theme: Theme) =>
  createStyles({
    timelineCard: {
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      display: 'grid',
      gap: theme.spacing(0.75),
    },
    timelineCritical: {
      borderLeft: `4px solid ${theme.palette.error.main}`,
    },
    timelineSelected: {
      borderColor: theme.palette.primary.main,
      background: theme.palette.action.selected,
    },
    timelineMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    timelineGroup: {
      padding: theme.spacing(1),
      background: theme.palette.action.hover,
      borderRadius: theme.shape.borderRadius,
    },
    timelineState: { margin: theme.spacing(2, 0) },
    timelineFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
  });

const timelineRevealStyles = (theme: Theme) =>
  createStyles({
    timelineMarkerReveal: {
      display: 'grid',
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(1),
      paddingTop: theme.spacing(1),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  });

export const timelineStyles = (theme: Theme) =>
  createStyles({
    ...timelineRevealStyles(theme),
    ...timelineDisclosureStyles(theme),
    ...timelineLayoutStyles(theme),
    ...timelineContentStyles(theme),
  });
